import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Dropdown } from "react-bootstrap";
import { useParams } from "react-router-dom";

import { format } from "date-fns";
import ordinal from "ordinal";
import dateFormat from "dateformat";

import { TabBar } from "src/components/tabbar";
import { Nav, Tab } from "react-bootstrap";
import Thumbnail from "src/components/thumbnail";
import DownloadExcel from "src/components/downloadExcel";
import Tooltip from "src/components/tooltip";
import { SimulationReportContext } from "../../context";
import { FileService } from "src/utils/file.service";
import { getFullName } from "src/utils/utils";
import { CallAssessmentParameterView } from "src/pages/coaching/components/callAssessmentParameterView";

import { FactCheckEvaluation, SimulationLearnerAttempt} from "src/types/coaching/learners";
import { fetchAttemptsForSimualtion, fetchLearnerReport} from "src/redux/actions/coaching/simulation";

import {
  buildSimulationLearnerReportWorkbook,
  SIMULATION_OVERALL_SCHEMA,
} from "./simulationLearnerReportExport";

import _ from "lodash";
import cx from "classnames";
import styles from "./detail.module.scss";


interface EditSimulationReportsDetailProps {
  learner: any;
}

export const EditSimulationReportsDetail: React.FC<EditSimulationReportsDetailProps> = ({ learner }) => {
  const reportContext = useContext(SimulationReportContext);
  const dispatch = useDispatch();
  const params = useParams();
  const courseId = params.id as string;

  const learners = useSelector((state: any) =>
    _.get(state.coaching, "simulations.attemptLearners.items", []),
  );

  const scores = useMemo(() => {
    return (
      learners.slice(0,3).map((a: any) => Math.round((a.score ?? 0) * 100)) || []
    );
  }, [learners]);

  const attempts: SimulationLearnerAttempt[] = learners.map((item, index, arr) => {
    return {
      ...item,
      attemptNumber: arr.length - index, 
    };
  });

  const initialAttempt = _.last(attempts);
  const currentAttempt = attempts.find((a) => a.id === learner.id);

  const learnerBestScore = useMemo(() => {
    return _.get(reportContext.simulation, "bestScore", 0);
  }, [reportContext.simulation]);

  const totalDuration = useMemo(() => {
    return _.sumBy(attempts, "duration");
  }, [attempts]);

  const bestScore = useMemo(() => {
    const best = _.maxBy(attempts, "score") || learner;
    return best ? Math.round(best.score * 100) : "NA";
  }, [attempts]);

  const handleToggleCallRecording = () => {
    if (reportContext.toggleCallRecording) {
      reportContext.toggleCallRecording();
    }
  };

  useEffect(() => {
    dispatch(
      fetchAttemptsForSimualtion({
        id: courseId,
        learnerId: learner.user.id,
      }),
    );
  }, [learner?.id]);

  return (
    <div className={styles.detailContainer}>
      <div className={styles.profileContainer}>
        {reportContext.showCallRecording && (
        <Tooltip content="Toggle sidebar" placement="right">
          <button className={styles.toggleButton} onClick={handleToggleCallRecording}>
            <i className={`paperflite-side-panel`}></i>
          </button>
        </Tooltip>
        )}
        <Thumbnail
          media={learner.user?.icon}
          defaultImage="/images/assets/default_asset.jpg"
          className={styles.profileAvatar}
        />
        <div className={styles.details}>
          <div className={styles.profileInfo}>
            <span className={styles.name}>
              {learner.user.firstName || ""} {learner.user.lastName}
            </span>
            <span className={styles.email}>{learner.user.email}</span>
          </div>
          {learner?.rank > 0 && (
          <div className={styles.rank}>
            <span className={styles.symbol}>#</span>
            <span className={styles.number}>{learner.rank}</span>
            <div className={styles.textContainer}>
              <span className={styles.name}>best score</span>
              <span className={styles.score}>{bestScore}%</span>
            </div>
          </div>
          )}
        </div>
      </div>
      <div className={styles.infoContainer}>
        <Metrics
          name="latest score"
          value={learner?.score ? `${Math.round(learner.score * 100)}%` : "NA"}
          subTitle={`average best score across the team is ${learnerBestScore ? Math.round(learnerBestScore * 100) : "na"}%`}
        />

        <Metrics
          name="attempts taken"
          value={learner.completedAttempts ? `#${learner.completedAttempts}` : "NA"}
          attemptsScores={scores}
        />
        <Metrics
          name="ramp-up time"
          value={FileService.getTimeToComplete(initialAttempt?.createdDate, initialAttempt?.modifiedDate)}
          subTitle={
            initialAttempt?.assignedBy
              ? `assigned on ${dateFormat(initialAttempt?.createdDate, "dd mmm yyyy")}`
              : `assigned on ${dateFormat(initialAttempt?.createdDate, "dd mmm yyyy")}`
          }
        />

        <Metrics
          name="time spent"
          value={FileService.formatDuration(totalDuration, "short")}
        />
      </div>
      <div className={styles.detailedReport}>
        <LearnerDetailsReport learner={currentAttempt} attempts={attempts}
        />
      </div>
    </div>
  );
};

interface MetricsProps {
  name: string;
  value: string;
  subTitle?: string;
  attemptsScores?: number[];
  
}

const Metrics: React.FC<MetricsProps> = ({
  name,
  value,
  subTitle,
  attemptsScores,
}) => {
  return (
    <div className={styles.infoCard}>
      <div className={styles.metrics}>
        <span className={styles.name}>{name}</span>
        <span className={styles.value}>{value}</span>

        {_.size(attemptsScores) > 1 && (
          <div className={styles.progressContainer}>
            {attemptsScores?.map((score: number, index: number) => {
              const isLatest = index === 0;
              return (
                <div key={index} className={styles.attemptProgress}>
                  <div
                    className={cx(styles.fill, {
                      [styles.lightFill]: !isLatest,
                    })}
                    style={{ width: `${score === 0 ? 1 : score}%` }}
                  />
                  <span className={styles.label}>{score}%</span>
                </div>
              );
            })}
          </div>
        )}

        {subTitle && <span className={styles.subTitle}>{subTitle}</span>}
      </div>
    </div>
  );
};

interface LearnerDetailsReportProps {
  learner: any;
  attempts: any[];
}

const LearnerDetailsReport: React.FC<LearnerDetailsReportProps> = ({
  learner,
  attempts,
}) => {
  const dispatch = useDispatch();
  const reportContext = useContext(SimulationReportContext);

  const [activeTab, setActiveTab] = useState(0);
  const [selectedAttempt, setSelectedAttempt] = useState(learner);

  const report = useSelector((state: any) =>
    _.get(state.coaching, "simulations.learnerReport.item.report", null),
  );

  const parameters = report?.parameters ?? [];
  const loading = useSelector((state: any) =>
    _.get(state.coaching, "simulations.learnerReport.loading", false),
  );
  const loaded = useSelector((state: any) =>
    _.get(state.coaching, "simulations.learnerReport.loaded", false),
  );
  const reportError = useSelector((state: any) =>
    _.get(state.coaching, "simulations.learnerReport.error", null),
  );

  const workbook = useMemo(() => buildSimulationLearnerReportWorkbook(report), [report]);

  const overallExportRows = useMemo(() => {
    if (!workbook?.data[0]) {
      return [];
    }
    return workbook.data[0];
  }, [workbook]);

  const exportPagination = useMemo(() => {
    const totalRows = workbook
      ? _.sumBy(workbook.data, (sheet) => _.size(sheet))
      : 1;
    return {
      currentPage: 0,
      totalPages: 1,
      recordsPerPage: Math.max(totalRows, 1),
      totalRecordsCount: Math.max(totalRows, 1),
      hasMoreItems: false,
    };
  }, [workbook]);

  const exportFileName = useMemo(() => {
    const simulationName = reportContext?.simulation?.name ?? "simulation";
    const learnerPart =
      getFullName(selectedAttempt?.user)?.trim() ||
      selectedAttempt?.user?.email ||
      "learner";
    const slug = (s: string) =>
      String(s)
        .replace(/[^a-zA-Z0-9-_]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 48) || "x";
    const attemptPart = String(selectedAttempt?.attemptNumber ?? 1);
    return `${slug(simulationName)}_${slug(learnerPart)}_${attemptPart}`;
  }, [
    reportContext?.simulation?.name,
    selectedAttempt?.user,
    selectedAttempt?.attemptNumber,
  ]);

  const exportFetchState = {
    loading,
    succeeded: loaded && !loading,
    error: !!reportError,
  };

  const handleToggleCallRecording = () => {
    if (reportContext.toggleCallRecording) {
      reportContext.toggleCallRecording();
    }
  };

  const onSelect = (attempt: any) => {
    setSelectedAttempt(attempt);
    reportContext.fetchTranscripts?.(attempt);
    dispatch(fetchLearnerReport({ id: attempt.id }));
  };

  const duration = useMemo(() => {
    if (reportContext?.simulation?.type === "elevator_pitch") {
      const duration = report?.duration || 0;
      const timeBound = reportContext.simulation.timeBound || 0;
      const rounded = duration >= timeBound
        ? Math.round(duration / 30) * 30  
        : duration;
      return FileService.formatTimeBound(rounded, "short");
    }
    return FileService.formatDuration(report?.duration, "short");
  }, [reportContext?.simulation, report?.duration]);

  useEffect(() => {
    if (learner) {
      dispatch(fetchLearnerReport({ id: learner.id }));
      setSelectedAttempt(learner);
      reportContext.fetchTranscripts?.(learner); 
    }
  }, [learner?.id]);

  return (
    <div className={styles.learnerPerformance}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <div className={styles.title}>breakdown</div>
          <div className={styles.details}>
            <div className={styles.metadata}>
              call logged on{" "}
              {dateFormat(selectedAttempt?.modifiedDate, "dd mmm yyyy")},{" "}
              {dateFormat(selectedAttempt?.modifiedDate, "HH:MM:ss")}
            </div>
            <div className={styles.divider} />
            <div className={styles.metadata}>
              duration {FileService.formatDuration(selectedAttempt?.duration, "short")}
            </div>
          </div>
        </div>
        <div className={styles.actions}>
        {
            report?.status !== "in_sufficient_duration" &&
            report?.status !== "failed" &&
            _.size(report?.parameters) > 0 && (
            <div className={styles.downloadButton}>
              <DownloadExcel
                title="Call Assessment Report"
                fileName={exportFileName}
                schema={SIMULATION_OVERALL_SCHEMA}
                data={overallExportRows}
                multiSheet={Boolean(workbook)}
                multiSheetData={workbook?.data}
                multiSheetSchemas={workbook?.schemas as any}
                multiSheetNames={workbook?.sheets}
                fetchState={exportFetchState}
                pagination={exportPagination}
                fetchDataForPage={() =>
                  dispatch(fetchLearnerReport({ id: selectedAttempt.id }))
                }
                invalidateData={() => {}}
              />
            </div>
          )}
          {!reportContext?.showCallRecording  &&
            learner?.status === "COMPLETED" &&
            report?.status !== "in_sufficient_duration" &&
            report?.status !== "failed" && (
              <div className={styles.callRecording}>
                <button className={styles.action} onClick={handleToggleCallRecording}>
                  <i className="paperflite-waveform"></i>
                  <span>call recording</span>
                </button>
              </div>
            )}

          {_.size(attempts) > 1 && (
            <div className={styles.attemtsFilterContainer}>
              <DropDown
                selected={selectedAttempt}
                attempts={attempts}
                onSelect={onSelect}
              />
            </div>
          )}
        </div>
      </div>
      {loading && <ReportPlaceholder />}

      {!loading && report?.status === "in_sufficient_duration" && (
        <div className={styles.emptyMessage}>
          <div className={styles.title}>data unavailable</div>
          <p className={styles.subTitle}>
            {" "}
            On the {attempts.length} attempt, the user dropped before 2 minutes
          </p>
        </div>
      )}

      {!loading && report?.status === "failed" && (
        <div className={styles.emptyMessage}>
          <div className={styles.title}>data unavailable</div>
          <p className={styles.subTitle}>
            {" "}
            On the {attempts.length} attempt,{" "}
            {reportContext?.simulation?.type === "elevator_pitch"
              ? "the rep's pitch did not meet the minimum duration required for analysis (15 seconds)."
              : "the rep did not engage in conversation for more than 2 minutes"}
          </p>
        </div>
      )}

      {!loading && report?.status !== "in_sufficient_duration" && report?.status !== "failed" && (
        <div className={styles.callAssessmentContainer}>
          <TabBar
            className={cx(styles.tabs)}
            gap={32}
            tabSize="equal"
            active={activeTab}
            onSelectionChange={(index) => setActiveTab(index)}
          >
            {parameters.map((parameter, index) => (
              <span
                key={parameter.uuid}
                className={cx(styles.tab, {
                  [styles.active]: activeTab === index,
                })}
              >
                <div className={styles.name}>
                  {parameter.name} ({Math.round(parameter.score)}/5)
                </div>
              </span>
            ))}
            {!loading &&
              _.size(report?.factChecks) > 0 &&
              _.size(parameters) > 0 && (
              <span
                key="fact-checker"
                className={cx(styles.tab, {
                  [styles.active]: activeTab === parameters.length,
                })}
              >
                <div className={styles.name}>Claim Validation</div>
              </span>
            )}
          </TabBar>
          <div className={styles.callAssessmentParamater}>
            {parameters.map(
              (parameter, index) =>
                activeTab === index && (
                  <div key={parameter.uuid}>
                    <CallAssessmentParameterView parameter={parameter} />
                  </div>
                ),
            )}
            {_.size(report?.factChecks) > 0 && _.size(parameters) > 0 && activeTab === parameters.length && (
              <CallAssessmentFactInsights
                factChecks={report.factChecks}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface DropDownProps {
  attempts: any[];
  selected: any;
  onSelect?: (item: any) => void;
}

export const DropDown: React.FC<DropDownProps> = ({
  attempts,
  selected,
  onSelect,
}) => {
  const [show, setShow] = React.useState(false);

  const handleSelect = (item: any) => {
    setShow(false);
    if (onSelect) {
      onSelect(item);
    }
  };

  if (!selected) {
    return (
      <div className={cx(styles.selectDropdown, styles.placeholder)}></div>
    );
  }

  return (
    <div className={styles.selectDropdown}>
      <Dropdown>
        <span onClick={() => setShow(!show)}>
          <Dropdown.Toggle className={styles.button} as={"button"}>
            <div className={styles.atttempt}>
              <span className={styles.score}>
                {`${Math.round(selected.score * 100)} %` || "na"}
              </span>
              <div className={styles.divider} />
              <span className={styles.attemptCount}>
                {ordinal(selected?.attemptNumber) || "na"} <span className={styles.text}>attempt</span>
              </span>
            </div>
            <i
              className={cx("paperflite-dropdown", {
                [styles.openModal]: show,
              })}
            ></i>
          </Dropdown.Toggle>
        </span>

        <Dropdown.Menu
          className={styles.dropdown}
          popperConfig={{
            modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
          }}
        >
          {attempts.map((attempt) => {
            const date = new Date(attempt?.modifiedDate).toISOString();
            const formattedDateTime = format(date, "do MMM hh:mm a");

            const active = attempt.id === selected.id;
            return (
              <Dropdown.Item
                key={attempt.attemptNumber}
                className={cx(styles.dropdownItem, { [styles.active]: active })}
                onClick={() => handleSelect(attempt)}
              >
                {String(attempt.attemptNumber).padStart(2, "0")} /{" "}
                {Math.round(attempt.score * 100)} % / {formattedDateTime}
                {active && <i className="paperflite-tick-selected"></i>}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

const ReportPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.callAssessmentContainer, styles.placeholder)}>
      <div className={cx(styles.tabs)}>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
      </div>

      <div className={cx(styles.verticalTabs, styles.placeholder)}>
        <span className={cx(styles.text, styles.block)}></span>
        <span className={cx(styles.text, styles.block)}></span>
        <span className={cx(styles.text, styles.block)}></span>
      </div>
    </div>
  );
};

interface CallAssessmentFactInsightsProps {
  factChecks: FactCheckEvaluation[];
}

const CallAssessmentFactInsights: React.FC<CallAssessmentFactInsightsProps> = ({
  factChecks
}) => {
  return (
    <div className={styles.callAssessmentParameterContainer}>

      <div className={styles.parameterTabContainer}>
        <Tab.Container defaultActiveKey="0">

          <Nav className={styles.tabs}>
            {factChecks.map((item, index) => (
              <Nav.Link
                key={index}
                className={styles.tab}
                eventKey={index}
              >
                <span>Claim {index + 1}</span>
              </Nav.Link>
            ))}
          </Nav>

          <Tab.Content className={styles.tabContent}>
            {factChecks.map((item, index) => {

              return (
                <Tab.Pane
                  className={styles.parameterContainer}
                  eventKey={index}
                  key={index}
                >
                  <div className={styles.parameter}>
                    <div className={styles.label}>Rep Statement <span className={styles.timestamp}>{item.timestamp}</span></div>
                    <div className={styles.value}>
                      {item?.claim}
                    </div>
                  </div>

                  {item?.feedback && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Knowledge Reference</div>
                      <div className={styles.value}>
                        {item?.feedback}
                      </div>
                    </div>
                  )}

                  {/* {evidence?.answer && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Verified answer</div>

                      <div className={styles.value}>
                        {evidence.answer}
                      </div>

                      {evidence.citations?.length > 0 && (
                        <div className={styles.citations}>
                          {evidence.citations.map((citation, i) => (
                            <div key={i} className={styles.citation}>
                              <span className={styles.citationIndex}>
                                {i + 1}
                              </span>
                              <span className={styles.citationText}>
                                {citation}
                              </span>
                              <i
                                className={cx(
                                  "paperflite-arrow-up",
                                  styles.citationArrow
                                )}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )} */}
                </Tab.Pane>
              );
            })}
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  );
};